import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Header } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Truck,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  Phone,
  Hash,
  DollarSign,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TruckersPage() {
  const [truckers, setTruckers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTrucker, setSelectedTrucker] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    rate: '',
    truck_number: '',
    phone: '',
    is_active: true,
  });

  useEffect(() => {
    fetchTruckers();
  }, []);

  const fetchTruckers = async () => {
    try {
      const response = await axios.get(`${API_URL}/truckers`);
      setTruckers(response.data);
    } catch (error) {
      toast.error('Failed to fetch truckers');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (trucker = null) => {
    if (trucker) {
      setSelectedTrucker(trucker);
      setFormData({
        name: trucker.name,
        rate: trucker.rate,
        truck_number: trucker.truck_number || '',
        phone: trucker.phone || '',
        is_active: trucker.is_active,
      });
    } else {
      setSelectedTrucker(null);
      setFormData({
        name: '',
        rate: '',
        truck_number: '',
        phone: '',
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (selectedTrucker) {
        await axios.put(`${API_URL}/truckers/${selectedTrucker.id}`, formData);
        toast.success('Trucker updated successfully');
      } else {
        await axios.post(`${API_URL}/truckers`, formData);
        toast.success('Trucker created successfully');
      }
      setDialogOpen(false);
      fetchTruckers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save trucker');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/truckers/${selectedTrucker.id}`);
      toast.success('Trucker deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedTrucker(null);
      fetchTruckers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete trucker');
    }
  };

  const filteredTruckers = truckers.filter((trucker) => {
    return trucker.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (trucker.truck_number && trucker.truck_number.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        title="Trucks"
        subtitle={`Manage your ${truckers.length} trucks`}
        actions={
          <Button onClick={() => handleOpenDialog()} className="gap-2" data-testid="add-trucker-btn">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Truck</span>
          </Button>
        }
      />

      <div className="flex-1 p-4 lg:p-8 space-y-6">
        {/* Search */}
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search trucks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-truckers"
              />
            </div>
          </CardContent>
        </Card>

        {/* Truckers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTruckers.map((trucker, index) => (
            <Card 
              key={trucker.id} 
              className="card-base animate-slide-up hover:shadow-md transition-shadow"
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`trucker-card-${trucker.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      trucker.is_active ? 'bg-secondary/10' : 'bg-muted'
                    }`}>
                      <Truck className={`w-6 h-6 ${
                        trucker.is_active ? 'text-secondary' : 'text-muted-foreground'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-semibold">{trucker.name}</h3>
                      <p className="text-sm text-muted-foreground">Truck</p>
                    </div>
                  </div>
                  <Badge variant={trucker.is_active ? 'default' : 'secondary'}>
                    {trucker.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <DollarSign className="w-4 h-4" />
                    <span>Rate: {trucker.rate}×</span>
                  </div>
                  {trucker.truck_number && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Hash className="w-4 h-4" />
                      <span>Truck: {trucker.truck_number}</span>
                    </div>
                  )}
                  {trucker.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      <span>{trucker.phone}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleOpenDialog(trucker)}
                    data-testid={`edit-trucker-${trucker.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => {
                      setSelectedTrucker(trucker);
                      setDeleteDialogOpen(true);
                    }}
                    data-testid={`delete-trucker-${trucker.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredTruckers.length === 0 && (
          <Card className="card-base">
            <CardContent className="p-8 text-center">
              <Truck className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">
                {searchQuery
                  ? 'No truckers match your search.'
                  : 'No truckers yet. Add your first trucker to get started.'}
              </p>
              {!searchQuery && (
                <Button onClick={() => handleOpenDialog()} className="mt-4" data-testid="add-first-trucker">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Trucker
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedTrucker ? 'Edit Truck' : 'Add New Truck'}
            </DialogTitle>
            <DialogDescription>
              {selectedTrucker ? 'Update truck information.' : 'Add a new truck to your farm group.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter truck name"
                  required
                  data-testid="trucker-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rate">Truck Rate (Multiplier) *</Label>
                <Input
                  id="rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                  placeholder="1.0"
                  required
                  data-testid="trucker-rate-input"
                />
                <p className="text-xs text-muted-foreground">
                  Amount = rate × tons (e.g., 1.0 means full tons)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="truck_number">Truck Number</Label>
                <Input
                  id="truck_number"
                  value={formData.truck_number}
                  onChange={(e) => setFormData({ ...formData, truck_number: e.target.value })}
                  placeholder="TRK-101"
                  data-testid="trucker-number-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+501 123-4567"
                  data-testid="trucker-phone-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-input"
                  data-testid="trucker-active-checkbox"
                />
                <Label htmlFor="is_active" className="cursor-pointer">
                  Active Truck
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-trucker-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Trucker'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Truck</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTrucker?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-trucker"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
